import { useQuery } from '@tanstack/react-query';
import { get, qs } from './client';
import { POLL_MS } from '../config';
import type {
  Alert,
  AlertStats,
  DeployHistoryEntry,
  Me,
  Member,
  WebhookModeInfo,
  WebhookStats,
  WebhookCategory,
  RaffleSummary,
  RafflesPage,
  Scraper,
  ScraperOutput,
  ScraperRun,
  ScraperSource,
  SocialKeywords,
  SocialStatus,
  SystemSummary,
  WorkerHealth,
} from './types';

export const keys = {
  me: ['me'] as const,
  scrapers: ['scrapers'] as const,
  scraperHistory: (name: string) => ['scraper', name, 'history'] as const,
  scraperSource: (name: string) => ['scraper', name, 'source'] as const,
  scraperOutput: (name: string) => ['scraper', name, 'output'] as const,
  systemSummary: ['system-summary'] as const,
  workerHealth: ['worker-health'] as const,
  raffles: (params: RaffleParams) => ['raffles', params] as const,
  raffleSummary: (hours: number) => ['raffles', 'summary', hours] as const,
  alerts: ['alerts'] as const,
  alertStats: ['alerts', 'stats'] as const,
  socialStatus: ['social', 'status'] as const,
  socialKeywords: ['social', 'keywords'] as const,
  webhookCategories: ['webhook-categories'] as const,
  scraperRouting: (name: string) => ['scraper', name, 'routing'] as const,
};

export function useMe() {
  return useQuery({
    queryKey: keys.me,
    queryFn: () => get<Me>('/api/me'),
    staleTime: Infinity, // identity does not change within a session
  });
}

export function useScrapers() {
  return useQuery({
    queryKey: keys.scrapers,
    // Socket events patch this cache in between; polling is the safety net.
    queryFn: () => get<Scraper[]>('/api/scrapers'),
    refetchInterval: POLL_MS,
  });
}

export function useSystemSummary() {
  return useQuery({
    queryKey: keys.systemSummary,
    queryFn: () => get<SystemSummary>('/api/system-summary'),
    refetchInterval: POLL_MS,
  });
}

export function useWorkerHealth() {
  return useQuery({
    queryKey: keys.workerHealth,
    // Returns 503 when the worker is dead, which is information, not an error.
    queryFn: () => get<WorkerHealth>('/api/worker-health').catch(() => null),
    refetchInterval: POLL_MS,
  });
}

/** Run history for one scraper. Also supplies the exec-time sparkline, which
 *  replaces the prototype's Math.random() "throughput" series. */
export function useScraperHistory(name: string | null) {
  return useQuery({
    queryKey: keys.scraperHistory(name ?? ''),
    queryFn: () =>
      get<{ history: ScraperRun[] }>(
        `/api/scraper/${encodeURIComponent(name!)}/history`,
      ).then((r) => r.history),
    enabled: !!name,
  });
}

export function useScraperSource(name: string | null) {
  return useQuery({
    queryKey: keys.scraperSource(name ?? ''),
    queryFn: () => get<ScraperSource>(`/api/scraper/${encodeURIComponent(name!)}`),
    enabled: !!name,
    staleTime: Infinity, // only changes when we save it
  });
}

/** Seeds the log pane; live lines then arrive over the socket. */
export function useScraperOutputSnapshot(name: string | null) {
  return useQuery({
    queryKey: keys.scraperOutput(name ?? ''),
    queryFn: () => get<ScraperOutput>(`/api/scraper/${encodeURIComponent(name!)}/output`),
    enabled: !!name,
  });
}

export interface RaffleParams {
  q?: string;
  status?: string;
  site?: string;
  scraper?: string;
  active?: boolean;
  sort?: string;
  order?: string;
  limit?: number;
  offset?: number;
}

export function useRaffles(params: RaffleParams) {
  return useQuery({
    queryKey: keys.raffles(params),
    queryFn: () => get<RafflesPage>(`/api/raffles${qs({ ...params })}`),
    refetchInterval: POLL_MS,
  });
}

export function useRaffleSummary(hours = 12) {
  return useQuery({
    queryKey: keys.raffleSummary(hours),
    queryFn: () => get<RaffleSummary>(`/api/raffles/summary${qs({ hours })}`),
    refetchInterval: POLL_MS,
  });
}

export function useAlerts() {
  return useQuery({
    queryKey: keys.alerts,
    queryFn: () => get<{ alerts: Alert[] }>('/api/alerts').then((r) => r.alerts),
    refetchInterval: POLL_MS,
  });
}

export function useAlertStats() {
  return useQuery({
    queryKey: keys.alertStats,
    queryFn: () => get<AlertStats>('/api/alerts/stats'),
    refetchInterval: POLL_MS,
  });
}

export function useSocialStatus() {
  return useQuery({
    queryKey: keys.socialStatus,
    queryFn: () => get<SocialStatus>('/api/social/status'),
  });
}

export function useMembers() {
  return useQuery({
    queryKey: ['members'],
    // Super-admin only — a 403 here is expected for everyone else.
    queryFn: () => get<Member[]>('/api/members'),
    retry: false,
  });
}

export function useWebhookMode() {
  return useQuery({
    queryKey: ['webhooks', 'mode'],
    queryFn: () => get<WebhookModeInfo>('/api/webhooks/mode'),
  });
}

export function useWebhookStats() {
  return useQuery({
    queryKey: ['webhooks', 'stats'],
    queryFn: () => get<WebhookStats>('/api/webhooks/stats'),
    refetchInterval: POLL_MS,
  });
}

export function useWebhookCategories() {
  return useQuery({
    queryKey: keys.webhookCategories,
    queryFn: () => get<{ categories: WebhookCategory[] }>('/api/webhook-categories').then((r) => r.categories),
  });
}

export function useScraperRouting(name: string) {
  return useQuery({ queryKey: keys.scraperRouting(name), queryFn: () => get<{ category_slug: string; override_enabled: boolean }>(`/api/scraper/${encodeURIComponent(name)}/routing`) });
}

export function useDeployAccess() {
  return useQuery({
    queryKey: ['deploy', 'access'],
    queryFn: () => get<{ allowed: boolean }>('/api/deploy/access'),
  });
}

export function useDeployHistory() {
  return useQuery({
    queryKey: ['deploy', 'history'],
    queryFn: () =>
      get<{ history: DeployHistoryEntry[] }>('/api/deploy/history').then((r) => r.history),
  });
}

export function useAlertConfig() {
  return useQuery({
    queryKey: ['alerts', 'config'],
    queryFn: () => get<{ webhook_url: string; url_suffix: string }>('/api/alerts/config'),
  });
}

export function useSocialKeywords() {
  return useQuery({
    queryKey: keys.socialKeywords,
    queryFn: () => get<SocialKeywords>('/api/social/keywords'),
    staleTime: Infinity, // baked into the worker source
  });
}
